export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
export const GOOGLE_ANALYTICS_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;

export const TIDE_PARAMETER = "sea_surface_height_amplitude_due_to_geocentric_ocean_tide_geoid_mllw";

// variables we wish to retrieve from ERDDAP, but don't directly display in UI
export const HIDDEN_OBS_VARIABLES = [
  "time",
  "latitude",
  "longitude",
  "z",
  "station",
];

// variables we don't need to retrieve from ERDDAP
export const EXCLUDED_OBS_VARIABLES = [
  "battery_voltage",
  "sea_water_electrical_conductivity",
  "sea_water_practical_salinity",
  "solar_irradiance_cm_time__mean",
  "solar_irradiance_cm_time__maximum_over_pt1h",
  "solar_irradiance_cm_time__mean_over_pt1h",
  "solar_irradiance",
  "air_temperature_cm_time__maximum_over_pt1h",
  "air_temperature_cm_time__minimum_over_pt1h",
  "air_temperature_cm_time__mean_over_pt1h_mean",
  "surface_temperature_cm_time__mean_over_pt1h",
  "solar_irradiance_cm_time__maximum_over_pt1h_max",
  "solar_irradiance_cm_time__minimum_over_pt1h_min",
  "lwe_thickness_of_precipitation_amount_15_minute_total",
  "lwe_precipitation_rate_cm_time__sum_over_pt1h",
  "surface_snow_thickness",
  "depth_reading",
  "relative_humidity_cm_time__mean_over_pt1h_mean",
];
export const EXCLUDED_OBS_VARIABLE_SUFFIXES = ["_qc_agg", "_qc_tests"];

// mappings from ERDDAP and js-quantities units to UI display units
export const UNIT_MAPPINGS = {
  degree_Celsius: "°C",
  degree_Fahrenheit: "°F",
  tempC: "°C",
  tempF: "°F",
  "m.s-1": "m/s",
  "mile.hour-1": "mph",
  degrees: "°",
  feet: "ft",
  seconds: "s",
  second: "s",
  meters: "m",
  millibars: "mb",
};

export const DATE_FORMAT_OPTIONS = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
  timeZoneName: "short",
};

export const QC_SUFFIX = "_qc_agg";

const QC_FLAGS = {
  PASS: 1,
  NOT_EVALUATED: 2,
  SUSPECT: 3,
  FAIL: 4,
  MISSING: 9,
};

export const OLD_DATA_THRESHOLD = 1000 * 60 * 60 * 4; // 4 hours in milliseconds

export const ACCEPTED_QC_FLAGS = [QC_FLAGS.PASS, QC_FLAGS.NOT_EVALUATED];

export const UNIT_SYSTEMS = {
  imperial: "imperial",
  metric: "metric",
};

// Some units from ERDDAP, like "degree_Celsius" are not recognized by js-quantities,
// so we need to map them to a recognized unit
export const canonicalUnitsToJsQty = {
  "°": "degrees",
  "mb": "millibars",
  "°C": "tempC",
  degree_Celsius: "tempC",
  degree_Fahrenheit: "tempF",
  "m.s-1": "m/s",
  "mile.hour-1": "mph",
};

export const categories = {
  waves: "Waves & Swell",
  wind_weather: "Winds & Weather",
  water: "Water & Ocean Conditions",
  currents: "Currents",
  land: "Land & Surface Conditions",
};

// displayName primarily used for definitions page - prefer the name directly from ERDDAP metadata when possible
export const parameterPreferredUnits = {
  air_pressure_at_mean_sea_level: {
    metric: "millibars",
    imperial: "millibars",
    definition: "CF: Air pressure is the force per unit area which would be exerted when the moving gas molecules of which the air is composed strike a theoretical surface of any orientation.",
    category: categories.wind_weather,
    displayName: "Air Pressure At Sea Level",
  },
  air_pressure: {
    metric: "millibars",
    imperial: "millibars",
  },
  depth_reading: {
    metric: "meters",
    imperial: "feet",
  },
  air_temperature: {
    metric: "tempC",
    imperial: "tempF",
    definition: "CF: The bulk temperature of the air, not just the surface (skin) temperature.",
    category: categories.wind_weather,
    displayName: "Air Temperature",
  },
  sea_surface_temperature: {
    metric: "tempC",
    imperial: "tempF",
    definition: "CF: The temperature of sea water near the surface (including the part under sea-ice, if any).",
    category: categories.water,
    displayName: "Sea Surface Temperature",
  },
  sea_water_temperature: {
    metric: "tempC",
    imperial: "tempF",
    definition: "CF: Sea water temperature is the in situ temperature of the sea water.",
    category: categories.water,
    displayName: "Water Temperature",
  },
  sea_surface_wave_mean_period: {
    metric: "second",
    imperial: "second",
    definition: "CF: amount of time between repeated features on the waveform (ex. crests, troughs).",
    category: categories.waves,
    displayName: "Average Wave Period",
  },
  sea_surface_wave_period_at_variance_spectral_density_maximum: {
    metric: "second",
    imperial: "second",
    definition: "NBDC: period with maximum wave energy",
    category: categories.waves,
    displayName: "Dominant Wave Period",
  },
  sea_surface_wave_significant_height: {
    metric: "meters",
    imperial: "feet",
    definition: "CF: Calculated average height of the highest one third of the waves, where the height is defined as the vertical distance from a wave trough to the following wave crest.",
    category: categories.waves,
    displayName: "Significant Wave Height",
  },
  sea_surface_wave_to_direction: {
    metric: "degrees",
    imperial: "degrees",
  },
  sea_surface_swell_wave_significant_height: {
    metric: "meters",
    imperial: "feet",
    definition: "NBDC: Swell height is the vertical distance (meters) between any swell crest and the succeeding swell wave trough.",
    category: categories.waves,
    displayName: "Swell Height",
  },
  sea_surface_swell_wave_from_direction: {
    metric: "degrees",
    imperial: "degrees",
    definition: "NBDC: The direction from which the swell waves at the swell wave period are coming. The units are degrees from true North, increasing clockwise, with North as 0 (zero) degrees and East as 90 degrees.",
    category: categories.waves,
    displayName: "Swell Wave From Direction",
  },
  sea_surface_wind_wave_from_direction: {
    metric: "degrees",
    imperial: "degrees",
    definition: "CF: The direction from which the horizontal component of the wind is blowing. A wind blowing from the west is similar to saying “westerly”, and is the opposite of a “westward” wind.",
    category: categories.waves,
    displayName: "Wind Wave From Direction",
  },
  sea_surface_swell_wave_period: {
    metric: "second",
    imperial: "second",
  },
  sea_surface_wind_wave_to_direction: {
    metric: "degrees",
    imperial: "degrees",
  },
  sea_surface_wind_wave_significant_height: {
    metric: "meters",
    imperial: "feet",
    definition: "NBDC: vertical distance (meters) between any wind wave crest and the succeeding wind wave trough (independent of swell waves)",
    category: categories.waves,
    displayName: "Wind Wave Height",
  },
  sea_surface_wind_wave_period: {
    metric: "second",
    imperial: "second",
  },
  wind_speed_of_gust: {
    metric: "m/s",
    imperial: "knots",
    definition: "CF: The wind speed is the magnitude of the wind velocity. A gust is a sudden brief period of high wind speed.",
    category: categories.wind_weather,
    displayName: "Wind Gust",
  },
  // TODO: differentiate between wind_speed_of_gust and wind_speed_of_gust_max
  wind_speed_of_gust_max: {
    metric: "m/s",
    imperial: "knots",
    category: categories.wind_weather,
    displayName: "Max Wind Gust",
  },
  wind_gust_from_direction: {
    metric: "degrees",
    imperial: "degrees",
    category: categories.wind_weather,
    displayName: "Wind Gust From Direction",
  },
  wind_speed: {
    metric: "m/s",
    imperial: "knots",
    definition: "CF: The magnitude of the horizontal wind velocity.",
    category: categories.wind_weather,
    displayName: "Wind Speed",
  },
  wind_from_direction: {
    metric: "degrees",
    imperial: "degrees",
    definition: "NBDC: The direction the wind is coming from in degrees clockwise from true N.",
    category: categories.wind_weather,
    displayName: "Wind From Direction",
  },
  dew_point_temperature: {
    metric: "tempC",
    imperial: "tempF",
    definition: "CF: The temperature at which a parcel of air reaches saturation upon being cooled at constant pressure and specific humidity.",
    category: categories.wind_weather,
    displayName: "Dew Point",
  },
  relative_humidity: {
    metric: "%",
    imperial: "%",
    definition: "CF: Amount of moisture in the air as vapor relative to how much it can possibly hold at the same temperature.",
    category: categories.wind_weather,
    displayName: "Relative Humidity",
  },
  sea_water_electrical_conductivity: {
    metric: "mS/cm",
    imperial: "μmho/cm",
  },
  sea_surface_height_amplitude_due_to_geocentric_ocean_tide_geoid_mllw: {
    metric: "m",
    imperial: "ft",
  },
  sea_surface_height_above_sea_level_geoid_mllw: {
    metric: "m",
    imperial: "ft",
  },
  lwe_thickness_of_precipitation_amount: {
    metric: "mm",
    imperial: "in",
    definition: "",
    category: categories.wind_weather,
    displayName: "Precipitation (accumulation)",
  },
  surface_snow_thickness: {
    metric: "meters",
    imperial: "feet",
  },
  sea_water_velocity_to_direction: {
    metric: "degrees",
    imperial: "degrees",
    definition: "NBDC: The direction the ocean current is flowing toward. 0-360 degrees, 360 is due north, 0 means no measurable current.",
    category: categories.currents,
    displayName: "Current To Direction",
  },
  sea_water_speed: {
    metric: "m/s",
    imperial: "knots",
    definition: "NBDC: Speed of the ocean current.",
    category: categories.currents,
    displayName: "Current Speed",
  },
  eastward_sea_water_velocity: {
    metric: "m/s",
    imperial: "knots",
    definition: "CF: Velocity (speed) of the sea water in the east-west dimension. Positive values mean the net velocity is moving towards the east (negative values mean a net movement to the west).",
    category: categories.currents,
    displayName: "Eastward Sea Water Velocity",
  },
  northward_sea_water_velocity: {
    metric: "m/s",
    imperial: "knots",
    definition: "CF: Velocity (speed) of the sea water in the north-south dimension. Positive values mean the net velocity is moving towards the north (negative values mean a net movement to the south).",
    category: categories.currents,
    displayName: "Northward Sea Water Velocity",
  },
  upward_sea_water_velocity: {
    metric: "m/s",
    imperial: "knots",
    definition: "CF: Velocity (speed) of the sea water in the up-down dimension. Positive values mean the net velocity is moving up, away from the center of the Earth (negative values mean a net movement down towards the center of the Earth).",
    category: categories.currents,
    displayName: "Upward Sea Water Velocity",
  },
  sea_water_pressure: {
    metric: "decibars",
    imperial: "decibars",
    definition: "CF: Pressure that exists in the medium of sea water. It includes the pressure due to overlying sea water, sea ice, air and any other medium that may be present.",
    category: categories.water,
    displayName: "Sea Water Pressure",
  },
  sea_surface_wave_directional_spread: {
    metric: "degrees",
    imperial: "degrees",
  },
  sea_surface_wave_from_direction: {
    metric: "degrees",
    imperial: "degrees",
  },
  sea_surface_wave_maximum_height: {
    metric: "meters",
    imperial: "feet",
    definition: "CF: Wave height is defined as the vertical distance from a wave trough to the following wave crest. The maximum wave height is the greatest trough to crest distance measured during the observation period.",
    category: categories.waves,
    displayName: "Maximum Wave Height",
  },
  visibility_in_air: {
    metric: "km",
    imperial: "miles",
    definition: "CF: The distance at which an object can be seen. This is a measure of the clarity of the atmospheric conditions.",
    category: categories.wind_weather,
    displayName: "Visibility",
  },
  soil_moisture_percent: {
    metric: "%",
    imperial: "%",
    definition: "",
    category: categories.land,
    displayName: "Soil Moisture",
  },
  soil_temperature: {
    metric: "tempC",
    imperial: "tempF",
    definition: "CF: The temperature at any given depth (or in a layer) below the surface of the ground, excluding surficial snow and ice (but not permafrost or soil).",
    category: categories.land,
    displayName: "Ground Temperature",
  },
  sea_surface_height_above_sea_level_geoid_mhhw: {
    metric: "m",
    imperial: "ft",
  }, 
  sea_surface_height_amplitude_due_to_geocentric_ocean_tide_above_mllw: {
    metric: "m",
    imperial: "ft",
    definition: "Tide predictions are derived mathematically from numerous inputs, including historical or nearby stations where real-time observations have been or are currently being recorded. Tide predictions only account for the astronomical tide and do not account for weather.",
    category: categories.water,
    displayName: "Tide Prediction (MLLW)",
  },
  water_surface_above_mllw: {
    metric: "m",
    imperial: "ft",
    definition: "The real-time, observed height of the water surface at a specific location.",
    category: categories.water,
    displayName: "Water Level (MLLW)",
  }
};

export const METADATA_FILE_NAME = 'erddapMetadata.json';
export const OBS_FILE_NAME = 'erddapObsData.json';
export const REGIONS_GEOJSON = 'regionsGeo.json';
